import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, ToggleLeft, ToggleRight, Users, Link, Database, Upload, Headphones, DollarSign, Target, BarChart3, CreditCard } from 'lucide-react';
import { SubscriptionPlan } from '@/hooks/usePlans';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface PlanCardProps {
  plan: SubscriptionPlan;
  onEdit: (plan: SubscriptionPlan) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string, isActive: boolean) => void;
}

export function PlanCard({ plan, onEdit, onDelete, onToggleStatus }: PlanCardProps) {
  return (
    <TooltipProvider>
      <Card className={`h-full flex flex-col min-h-[600px] ${!plan.is_active ? 'opacity-60' : ''}`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2">
              {plan.name}
              {!plan.is_active && <Badge variant="secondary">Inativo</Badge>}
              {plan.stripe_product_id && 
               (plan.stripe_price_monthly_id || plan.stripe_price_annual_id || plan.stripe_price_lifetime_id) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="text-green-600 border-green-600">
                      <CreditCard className="h-3 w-3 mr-1" />
                      Stripe
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Integração Stripe configurada</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </CardTitle>
            <CardDescription className="mt-1 space-y-0.5">
              {plan.description && (
                <div className="text-sm mb-2">{plan.description}</div>
              )}
              <div>Criado em {new Date(plan.created_at).toLocaleDateString('pt-BR')}</div>
              {plan.updated_at && plan.updated_at !== plan.created_at && (
                <div className="text-xs">
                  Última edição em {new Date(plan.updated_at).toLocaleDateString('pt-BR')}
                </div>
              )}
              {!plan.is_active && plan.inactivated_at && (
                <div className="text-xs text-destructive font-medium">
                  Inativado em {new Date(plan.inactivated_at).toLocaleDateString('pt-BR')}
                </div>
              )}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 flex-1 flex flex-col">
        {/* Pricing */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <span>Preços</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="text-center p-2 bg-muted/50 rounded">
              <div className="text-xs text-muted-foreground">Mensal</div>
              <div className="font-semibold">R$ {plan.price_monthly.toFixed(2)}</div>
            </div>
            <div className="text-center p-2 bg-muted/50 rounded">
              <div className="text-xs text-muted-foreground">Anual</div>
              <div className="font-semibold">R$ {plan.price_annual.toFixed(2)}</div>
            </div>
            <div className="text-center p-2 bg-muted/50 rounded">
              <div className="text-xs text-muted-foreground">Vitalício</div>
              <div className="font-semibold">R$ {plan.price_lifetime.toFixed(2)}</div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Limits */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span>{plan.max_users} {plan.max_users === 1 ? 'usuário' : 'usuários'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Link className="h-4 w-4 text-muted-foreground" />
            <span>
              {plan.integrations_mode === 'customizable'
                ? `Até ${plan.max_integrations} integração(ões)`
                : `${plan.allowed_integrations.length} pré-definidas`}
            </span>
          </div>
        </div>

        {/* Metrics */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{plan.basic_metrics.length} métricas básicas</span>
          </div>
          {plan.advanced_metrics_enabled && (
            <div className="flex items-center gap-2 text-sm text-primary">
              <Database className="h-4 w-4" />
              <span>
                {plan.advanced_metrics_mode === 'customizable'
                  ? `Até ${plan.max_advanced_metrics} avançadas`
                  : `${plan.advanced_metrics.length} avançadas`}
              </span>
            </div>
          )}
        </div>

        {/* Goals */}
        {plan.goals_enabled && (
          <div className="flex items-center gap-2 text-sm">
            <Target className="h-4 w-4 text-muted-foreground" />
            <span>
              {plan.max_goals === 0 ? 'Metas ilimitadas' : `Até ${plan.max_goals} meta(s)`}
            </span>
          </div>
        )}

        {/* Features */}
        <div className="flex flex-wrap gap-2">
          {plan.manual_entry_enabled && (
            <Badge variant="outline" className="text-xs">
              Lançamento Manual
            </Badge>
          )}
          {plan.bulk_import_enabled && (
            <Badge variant="outline" className="text-xs">
              <Upload className="h-3 w-3 mr-1" />
              Importação em Massa
            </Badge>
          )}
          {plan.priority_support && (
            <Badge variant="outline" className="text-xs text-primary border-primary">
              <Headphones className="h-3 w-3 mr-1" />
              Suporte Prioritário
            </Badge>
          )}
        </div>

        <div className="flex gap-2 pt-2 mt-auto">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={plan.is_active ? "default" : "outline"}
                size="sm"
                onClick={() => onEdit(plan)}
                className="flex-1"
              >
                <Edit className="h-4 w-4 mr-1" />
                Editar
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Editar configurações do plano</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={plan.is_active ? "default" : "outline"}
                size="sm"
                onClick={() => onToggleStatus(plan.id, !plan.is_active)}
              >
                {plan.is_active ? (
                  <ToggleRight className="h-4 w-4" />
                ) : (
                  <ToggleLeft className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{plan.is_active ? 'Desativar plano' : 'Ativar plano'}</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onDelete(plan.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Excluir plano permanentemente</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </CardContent>
    </Card>
    </TooltipProvider>
  );
}
